


import { Component, Vue, Prop } from 'vue-property-decorator';
import { TransitionDirection, TransitionColor, TransitionMode } from '@/store/modules/transitions';
import { gsap } from 'gsap';
import { namespace } from 'vuex-class';
import { RouteNames } from '@/utils/router/router.models';

const transitionsModule = namespace('transitions');

@Component({})
export default class PageRouter extends Vue {
  @transitionsModule.State public panelHeight!: number;

  @transitionsModule.Action public setPanelHeight!: (height: number) => {};

  @Prop({ default: TransitionColor.Gray }) private color!: TransitionColor;
  @Prop({ default: TransitionMode.Background }) private mode!: TransitionMode;
  @Prop({ default: TransitionDirection.Forward }) private direction!: TransitionDirection;
  @Prop({ default: 'parent' }) private routerType!: 'parent' | 'children';
  @Prop() private trigger!: string;

  private RouteNames: typeof RouteNames = RouteNames;

  private isLeaveAnimating = false;

  private leaveDoneFn: () => void | undefined = () => {
    return;
  };

  private routeTransitionStates: {
    loader?: boolean;
    doneEvents: {
      enter: (() => void) | undefined;
      leave: (() => void) | undefined;
    };
    promises: {
      enter?: Promise<void>;
      leave?: Promise<void>;
    };
  }[] = [];

  // First state -> Route: onBeforeRouteLeave
  // onEnter isn't yet triggered (Guard / Resolver is in proccess)
  private onRouteLeave() {
    const promise: Promise<void> = new Promise(resolve => {
      this.animateLeave(this.$el as HTMLElement, resolve);
    });

    // Push new page transition
    this.routeTransitionStates.push({
      doneEvents: {
        enter: undefined,
        leave: undefined
      },
      promises: {
        enter: undefined,
        leave: promise
      }
    });

    promise.then(() => {
      // Last transition in array
      const lastTransition = this.routeTransitionStates[this.routeTransitionStates.length - 1];

      // Show loader in case of enter-transition wasn't yet started
      if (lastTransition && !lastTransition.promises.enter) {
        lastTransition.loader = true;

        this.animateLoader(true);
      }
    });
  }

  // Second state -> Transition: onLeave
  // onEnter isn't yet triggered (Guard / Resolver is done)
  private onLeave(element: HTMLElement, done: () => void) {
    // onRouteLeave isn't triggerd for child routes so we have to do it by ourself
    if (this.routerType === 'children') {
      this.onRouteLeave();
    }

    const lastTransition = this.routeTransitionStates.find(state => !state.doneEvents.leave);

    if (lastTransition) {
      lastTransition.doneEvents.leave = done;
    } else {
      // Call done directly in case of lastTransition wasn't found
      done();
    }
  }

  // Third state -> Transition: onEnter
  // onLeave was already triggered
  private onEnter(element: HTMLElement, done: () => void) {
    const promise: Promise<void> = new Promise(resolve => {
      switch (this.mode) {
        case TransitionMode.Background: {
          this.animateContentMove(element, resolve);
          break;
        }
        case TransitionMode.PanelTranslate: {
          this.animatePanelTranslate(element, resolve, 'enter');
          break;
        }
        case TransitionMode.PanelHeight: {
          this.animatePanelHeight(element, resolve, 'enter');
          break;
        }

        default: {
          resolve();
        }
      }
    });

    const lastTransition = this.routeTransitionStates.find(state => !state.doneEvents.enter);

    if (lastTransition) {
      lastTransition.promises.enter = promise;
      lastTransition.doneEvents.enter = done;

      if (lastTransition.loader) {
        this.animateLoader(false);
      }

      // Trigger leave and enter done events if animations are completed
      Promise.all([lastTransition.promises.leave, lastTransition.promises.enter]).then(() => {
        // Use call() beacuse leave and enter could be undefined
        lastTransition.doneEvents.leave?.call(this);
        lastTransition.doneEvents.enter?.call(this);
      });
    } else {
      // hide the loader here in any case
      this.animateLoader(false);
      // Call done directly in case of lastTransition wasn't found
      done();
    }
  }

  private animateLoader(show: boolean) {
    const loaderEl: HTMLElement | null = document.querySelector('.global-loader');

    if (loaderEl) {
      gsap.fromTo(loaderEl, { opacity: +!show }, { opacity: +show, duration: 0.5 });
    }
  }

  private animateLeave(element: HTMLElement, done: () => void) {
    switch (this.mode) {
      case TransitionMode.Background: {
        this.animateBackgroundBlocks(element, done);
        break;
      }
      case TransitionMode.PanelTranslate: {
        this.animatePanelTranslate(element, done, 'leave');
        break;
      }
      case TransitionMode.PanelHeight: {
        this.animatePanelHeight(element, done, 'leave');
        break;
      }

      default: {
        done();
      }
    }
  }

  // Background-blocks Animation
  private animateContentMove(element: HTMLElement, done: () => void) {
    const containerWrapperEl = element.querySelector('.page-router__container-wrapper') as HTMLElement;
    const containerEl = element.querySelector('.page-router__container') as HTMLElement;

    gsap
      .timeline({ onComplete: done })
      .fromTo(
        containerWrapperEl,
        { xPercent: this.direction === TransitionDirection.Forward ? -100 : 100 },
        { xPercent: 0, duration: 1.25, delay: 0.6 }
      )
      .fromTo(
        containerEl,
        { xPercent: this.direction === TransitionDirection.Forward ? 100 : -100 },
        { xPercent: 0, duration: 1.25 },
        '<'
      );
  }

  private animateBackgroundBlocks(element: HTMLElement, done: () => void) {
    const firstAnimationBlock = element.querySelector('.page-router__animation-block--first') as HTMLElement;
    const lastAnimationBlock = element.querySelector('.page-router__animation-block--last') as HTMLElement;

    element.style.position = 'absolute';

    gsap
      .timeline({
        onComplete: done
      })
      .fromTo(
        firstAnimationBlock,
        {
          xPercent: this.direction === TransitionDirection.Forward ? -100 : 100,
          backgroundColor: this.color === TransitionColor.Gray ? '#f2eeee' : '#fff'
        },
        { xPercent: 0, duration: 1.25 }
      )
      .fromTo(
        lastAnimationBlock,
        {
          xPercent: this.direction === TransitionDirection.Forward ? -100 : 100,
          backgroundColor: this.color === TransitionColor.Gray ? '#fff' : '#f2eeee'
        },
        { xPercent: 0, duration: 1.25 },
        0.3
      );
  }

  // Panel-translate Animation
  private animatePanelTranslate(element: HTMLElement, done: () => void, type: 'leave' | 'enter') {
    const panelEl = element.querySelector('.conf-container__panel') as HTMLElement;
    const graphicEl = element.querySelector('.conf-container__graphic') as HTMLElement;

    if (type === 'enter') {
      this.animatePanelTranslateEnter(panelEl, graphicEl, done);
    } else {
      element.style.position = 'absolute';

      this.animatePanelTranslateLeave(panelEl, graphicEl, done);
    }
  }

  private animatePanelTranslateEnter(panelEl: HTMLElement, graphicEl: HTMLElement, done: () => void) {
    gsap
      .timeline({ onComplete: done })
      .fromTo(
        panelEl,
        { yPercent: this.direction === TransitionDirection.Forward ? -100 : 100 },
        { yPercent: 0, duration: 1.5 }
      )
      .fromTo(
        graphicEl,
        { translateY: this.direction === TransitionDirection.Forward ? '-100px' : '100px', opacity: 0 },
        {
          translateY: 0,
          opacity: 1,
          duration: 1
        },
        0.25
      );
  }

  private animatePanelTranslateLeave(panelEl: HTMLElement, graphicEl: HTMLElement, done: () => void) {
    gsap
      .timeline({ onComplete: done })
      .fromTo(
        panelEl,
        { yPercent: 0 },
        { yPercent: this.direction === TransitionDirection.Forward ? 100 : -100, duration: 1.5 }
      )
      .fromTo(
        graphicEl,
        { translateY: 0, opacity: 1 },
        {
          translateY: this.direction === TransitionDirection.Forward ? '100px' : '-100px',
          opacity: 0,
          duration: 1
        },
        0.25
      );
  }

  // Panel-height animation
  private animatePanelHeight(element: HTMLElement, done: () => void, type: 'enter' | 'leave') {
    const panelEl = element.querySelector('.selection-panel__panel') as HTMLElement;
    const contentEl = element.querySelector('.selection-panel__content') as HTMLElement;

    if (type === 'enter') {
      this.animatePanelHeightEnter(panelEl, contentEl, done);
    } else {
      this.animatePanelHeightLeave(panelEl, contentEl, done);
    }
  }

  private animatePanelHeightEnter(panelEl: HTMLElement, contentEl: HTMLElement, done: () => void) {
    const height: number = panelEl.offsetHeight;

    gsap.set(panelEl, { opacity: 0 });

    this.routeTransitionStates[this.routeTransitionStates.length - 1].promises.leave?.then(() => {
      gsap.set(panelEl, { opacity: 1 });

      gsap
        .timeline({ onComplete: done })
        .fromTo(panelEl, { height: this.panelHeight }, { height: height, duration: 0.5 })
        .fromTo(contentEl, { opacity: 0 }, { opacity: 1, duration: 0.5 });
    });
  }

  private animatePanelHeightLeave(panelEl: HTMLElement, contentEl: HTMLElement, done: () => void) {
    this.setPanelHeight(panelEl.offsetHeight);

    gsap
      .timeline({ onComplete: done })
      .fromTo(contentEl, { opacity: 1 }, { opacity: 0, duration: 0.5 })
      .set(panelEl, { opacity: 0 });
  }
}
